/* global Marionette, define, jQuery */
/* eslint-disable no-var, object-shorthand */
/**
 * Fit a step to the viewport it is being read in.
 *
 * The promise the conversational display makes is one thing at a time, on one
 * screen. A single question keeps that promise by itself; a step holding a
 * whole group of fields — a multi-part part, a layout row — can outgrow the
 * screen on a laptop or a short browser window, and the visitor is then
 * scrolling through something that was supposed to be a single glance.
 *
 * So the step is measured against the space it has and, when it does not fit,
 * asked to occupy less of it. Three rules bound that:
 *
 * - It only ever shrinks. The reviewed design size is the maximum, not a
 *   starting point; a huge monitor gets the same step a laptop gets, centred,
 *   rather than a inflated one.
 * - It stops at a floor. Past `MINIMUM` the text is no longer comfortably
 *   readable, and shrinking further trades a real problem for a worse one.
 * - Below the floor the step simply scrolls, which the canvas already allows.
 *   Nothing is ever hidden, clipped, or made unreadable to keep a layout
 *   promise — the promise yields first.
 *
 * Touch-target sizes are deliberately outside this system (see the SCSS): a
 * step that fits because its buttons became hard to press has not been fitted.
 */
define( 'controllers/fit',[], function () {
	/**
	 * The smallest a step's text may be shrunk, as a share of the reader's own
	 * base text size.
	 *
	 * The floor is expressed against the ROOT font size rather than as a fixed
	 * ratio, because a fixed ratio quietly works against the reader: someone
	 * who has enlarged text — through the browser's default size, or by
	 * zooming, which shrinks the viewport this fit measures — asks for bigger
	 * type and would be handed a step that shrinks it back. Deriving the floor
	 * from their setting means the more they have enlarged text, the less this
	 * is willing to take away, reaching no shrinking at all once the step's
	 * own type would drop below what they chose.
	 *
	 * At the default 16px root this resolves to the same 0.8 it always was.
	 *
	 * @type {number}
	 */
	var FLOOR_OF_ROOT = 0.85;

	/**
	 * The floor to use if the reader's base size cannot be read.
	 *
	 * @type {number}
	 */
	var FALLBACK_MINIMUM = 0.8;

	/**
	 * Room left before a step is considered to be overflowing, in pixels.
	 *
	 * Sub-pixel layout rounding routinely reports a step one pixel taller than
	 * its container when nothing is wrong, and scaling the whole step to
	 * recover that pixel is visible churn for no gain.
	 *
	 * @type {number}
	 */
	var TOLERANCE = 2;

	/**
	 * How many measure-and-shrink passes a single fit may take.
	 *
	 * Three lands within a pixel or two on every step tried, and the loop
	 * stops early the moment the step fits or stops shrinking. The bound
	 * exists so a step that cannot settle — content that reflows taller as it
	 * narrows — costs a fixed amount of work rather than looping.
	 *
	 * @type {number}
	 */
	var PASSES = 3;

	var controller = Marionette.Object.extend( {
		/**
		 * @param {Object}   options        Controller options.
		 * @param {Function} options.lookup The view's scoped element lookup.
		 * @param {Function} options.isOn   Whether the display is switched on.
		 * @return {void}
		 */
		initialize: function ( options ) {
			// Looked up on each pass rather than held: the canvas does not
			// exist yet when this controller is created, and in multi-part the
			// markup inside it is replaced as the visitor moves.
			this.lookup = options.lookup;
			this.isOn = options.isOn;
			this.applied = 1;

			// Re-measured on resize because the thing being measured against
			// is the viewport: rotating a phone, opening dev tools, or the
			// mobile browser's address bar sliding away all change the answer.
			// Bound through a frame so a drag of the window edge measures once
			// per paint rather than once per event.
			this.onResize = this.schedule.bind( this );
			jQuery( window ).on( 'resize.nfcffit', this.onResize );
		},

		/**
		 * Watch the step for growth this view did not cause.
		 *
		 * A step is not finished changing height when it is rendered: core
		 * writes a validation message under a field the moment the visitor
		 * leaves it empty, and conditional logic can reveal a field without
		 * any step change. Both happen after a fit has already been decided,
		 * and without watching for them a step silently goes back to
		 * overflowing having once fitted.
		 *
		 * `busy` keeps this off its own tail: scaling the step changes its
		 * size, which is exactly what this observer reports, so measurements
		 * this controller caused itself are ignored.
		 *
		 * @return {void}
		 */
		observe: function () {
			if ( 'function' !== typeof window.ResizeObserver ) {
				return;
			}

			if ( ! this.observer ) {
				this.observer = new window.ResizeObserver(
					function () {
						if ( this.busy ) {
							return;
						}

						this.schedule();
					}.bind( this )
				);
			}

			// The canvas cannot report this: its own height is pinned by the
			// step it has to fill, so it stays exactly the same size while the
			// content inside it outgrows it. What has to be watched is that
			// content — and in multi-part it is replaced wholesale on every
			// move, so the subscription is renewed rather than set up once.
			this.observer.disconnect();

			var content = this.lookup( '.nf-cf__content' ).get( 0 );

			if ( content ) {
				this.observer.observe( content );
			}
		},

		/**
		 * Stop listening. Called when the view is torn down.
		 *
		 * @return {void}
		 */
		onDestroy: function () {
			jQuery( window ).off( 'resize.nfcffit', this.onResize );

			if ( this.observer ) {
				this.observer.disconnect();
				this.observer = null;
			}
		},

		/**
		 * Measure on the next frame.
		 *
		 * @return {void}
		 */
		schedule: function () {
			if ( this.pending ) {
				return;
			}

			this.pending = true;

			var run = function () {
				this.pending = false;
				this.apply();
			}.bind( this );

			if ( 'function' === typeof window.requestAnimationFrame ) {
				window.requestAnimationFrame( run );
			} else {
				window.setTimeout( run, 16 );
			}
		},

		/**
		 * The smallest scale this reader should be given.
		 *
		 * @return {number} A scale between 0 and 1.
		 */
		minimumFor: function () {
			var root = parseFloat(
				window.getComputedStyle( document.documentElement ).fontSize
			);

			if ( ! root || ! this.baseFontPx ) {
				return FALLBACK_MINIMUM;
			}

			return Math.min( 1, ( root * FLOOR_OF_ROOT ) / this.baseFontPx );
		},

		/**
		 * Record the step's body text size while it is at full size.
		 *
		 * Read here and nowhere else, because everything downstream compares
		 * against the size the DESIGN asks for. Reading it later would read a
		 * size this controller had already reduced, and the floor would drift
		 * down with each pass — the reader's guarantee quietly weakening the
		 * more the step was shrunk.
		 *
		 * @param {Object} canvas The canvas element, as jQuery.
		 * @return {void}
		 */
		recordBaseFont: function ( canvas ) {
			var content = canvas.find( '.nf-cf__content' ).get( 0 );

			this.baseFontPx = content
				? parseFloat( window.getComputedStyle( content ).fontSize )
				: 0;
		},

		/**
		 * Measure the current step and scale it to fit.
		 *
		 * Measuring happens at full size, because the question being asked is
		 * "how much room does this step want", and a step already scaled from
		 * a previous measurement would answer about its scaled self and creep
		 * smaller on every pass.
		 *
		 * @return {void}
		 */
		apply: function () {
			var canvas = this.lookup( '.nf-cf__canvas' );
			var node = canvas && canvas.get( 0 );

			if ( ! node || ! this.isOn() ) {
				return;
			}

			this.observe();

			this.busy = true;
			this.write( canvas, 1 );
			this.recordBaseFont( canvas );

			// Cleared after the frame in which this fit's own size changes are
			// reported, so the observer hears the next real one.
			var release = function () {
				this.busy = false;
			}.bind( this );

			if ( 'function' === typeof window.requestAnimationFrame ) {
				window.requestAnimationFrame( release );
			} else {
				window.setTimeout( release, 32 );
			}

			if ( ! node.clientHeight ) {
				return;
			}

			// Approached over a few passes rather than solved in one, because
			// height is not proportional to the scale: touch targets hold
			// their size, text rewraps at a different width, and a line that
			// stops wrapping gives back more than its share. One pass
			// therefore lands short and leaves a step scrolling that could
			// have fitted. Each pass measures what the last one actually
			// produced, so the estimate converges instead of compounding.
			var scale = 1;

			for ( var pass = 0; pass < PASSES; pass++ ) {
				var available = node.clientHeight;
				var wanted = node.scrollHeight;

				if ( wanted <= available + TOLERANCE ) {
					return;
				}

				var next = Math.max(
					this.minimumFor(),
					scale * ( available / wanted )
				);

				if ( next >= scale ) {
					// At the floor, or no longer making progress. The step
					// scrolls, which is the honest outcome.
					return;
				}

				scale = next;
				this.write( canvas, scale );
			}
		},

		/**
		 * Scale the controls whose height the stylesheet cannot reach.
		 *
		 * A textarea is the tallest thing on most steps and the one thing the
		 * `fit()` scale does not touch: its height comes from core's own rules
		 * and from the visitor dragging its resize handle, not from any length
		 * this add-on declares, so a step could sit at the scale floor with
		 * two thirds of its height untouched — and still overflow. Its natural
		 * height is therefore measured once per fit and capped in proportion.
		 *
		 * `max-height` rather than `height`, so a textarea that is already
		 * shorter than its cap is left exactly as it is.
		 *
		 * @param {Object} canvas The canvas element, as jQuery.
		 * @param {number} scale  The multiplier.
		 * @return {void}
		 */
		capTallControls: function ( canvas, scale ) {
			canvas.find( 'textarea' ).each( function ( index, node ) {
				if ( 1 === scale ) {
					node.style.maxHeight = '';
					delete node.dataset.nfCfNaturalHeight;
					return;
				}

				// Recorded on the first shrink of this fit, while the step is
				// still at full size; reading it again after scaling would
				// measure the cap this put there and ratchet downwards.
				if ( ! node.dataset.nfCfNaturalHeight ) {
					node.dataset.nfCfNaturalHeight = String(
						node.getBoundingClientRect().height
					);
				}

				var natural = parseFloat( node.dataset.nfCfNaturalHeight );

				if ( natural ) {
					node.style.maxHeight = natural * scale + 'px';
				}
			} );
		},

		/**
		 * Put a scale on the canvas, if it is not already there.
		 *
		 * @param {Object} canvas The canvas element, as jQuery.
		 * @param {number} scale  The multiplier.
		 * @return {void}
		 */
		write: function ( canvas, scale ) {
			if ( scale === this.applied ) {
				return;
			}

			this.applied = scale;
			canvas.css( '--nf-cf-fit', String( scale ) );
			this.capTallControls( canvas, scale );
		},
	} );

	return controller;
} );

